import { Fragment, ReactNode } from "react";

// Orden de columnas elegido por el usuario (drag and drop en "Columnas"). Lo
// comparten las tablas de Postulaciones y Formularios. El orden guarda TODAS las
// columnas, visibles u ocultas, para que una columna oculta vuelva a su lugar al
// mostrarla. La visibilidad sigue viviendo aparte (visibleColumnKeys).

/**
 * Orden guardado → orden válido para las columnas actuales: descarta claves
 * desconocidas y repetidas, y agrega las columnas que no estaban (p. ej. una
 * columna nueva) justo después de la columna que las precede en el orden por
 * defecto, para que no aparezcan al final de la tabla.
 */
export const mergeColumnOrder = <K extends string>(saved: unknown, canonical: readonly K[]): K[] => {
  const known = new Set<string>(canonical);
  const result: K[] = [];
  if (Array.isArray(saved)) {
    saved.forEach((key) => {
      if (typeof key === "string" && known.has(key) && !result.includes(key as K)) result.push(key as K);
    });
  }
  canonical.forEach((key, index) => {
    if (result.includes(key)) return;
    const previous = canonical.slice(0, index).reverse().find((candidate) => result.includes(candidate));
    result.splice(previous === undefined ? 0 : result.indexOf(previous) + 1, 0, key);
  });
  return result;
};

/** Mueve `fromKey` a la posición que ocupa `toKey` (como al soltarla encima). */
export const moveColumn = <K extends string>(order: readonly K[], fromKey: K, toKey: K): K[] => {
  const from = order.indexOf(fromKey);
  const to = order.indexOf(toKey);
  if (from === -1 || to === -1 || from === to) return [...order];
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, fromKey);
  return next;
};

export const sameColumnOrder = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((key, index) => key === b[index]);

export const columnOrderStorageKey = (columnsStorageKey: string): string => `${columnsStorageKey}:order`;

export const readColumnOrder = <K extends string>(storageKey: string, canonical: readonly K[]): K[] => {
  try {
    return mergeColumnOrder(JSON.parse(localStorage.getItem(storageKey) ?? "null"), canonical);
  } catch {
    return [...canonical];
  }
};

/**
 * Celdas de una fila en el orden de las columnas visibles. Cada celda es una
 * función para no armar las que están ocultas.
 */
export const renderOrderedCells = <K extends string>(
  columns: readonly { key: K }[],
  cells: Record<K, () => ReactNode>,
): ReactNode => columns.map(({ key }) => <Fragment key={key}>{cells[key]()}</Fragment>);
